import { Channel, Topic } from "../../constants";
import {
  GeneralUtils,
  MessageBrokerUtils,
  PageContextUtils,
  TrackingUtils
} from "../../utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppContentHider, useScript } from "../../hooks";

import { EmptyComponent } from "../common/EmptyComponent";
import Loadable from "react-loadable";
import UiSelector from "../../redux/selectors/uiSelectors";
import { UserContextSelectors } from "../../redux/selectors";
import { useSelector } from "react-redux";
import { useSurvey } from "./useSurvey";

const SurveyOnDemand = Loadable({
  loader: () => import(/* webpackChunkName: "container/survey" */ "./Survey"),
  loading: EmptyComponent
});

const toggleBetslipVisibility = show => {
  MessageBrokerUtils.publish({
    channel: Channel.PLAYER,
    topic: Topic.showBetslip,
    headers: { publisher: "survey-betslip" },
    data: { show }
  });
};

const subscribeToNavigation = callback =>
  MessageBrokerUtils.subscribe({
    channel: Channel.ROUTER,
    topic: Topic.Navigate,
    callback
  });

const subscribeToOpenSurvey = callback =>
  MessageBrokerUtils.subscribe({
    channel: Channel.CRM,
    topic: Topic.OPEN_SURVEY,
    callback
  });

const createClientInitSubscription = callback =>
  MessageBrokerUtils.subscribe({
    channel: Channel.SESSION,
    topic: Topic.clientInitSucceeded,
    callback
  });

const toggleSpinner = (visible, publisher, clearError = true) =>
  MessageBrokerUtils.publish({
    channel: Channel.VIEW_INJECTOR,
    topic: Topic.SetVisible,
    headers: { publisher: publisher || "Survey" },
    data: { visible, clearError }
  });

const addToBetslip = betSlipData =>
  MessageBrokerUtils.publish({
    channel: Channel.PLAYER,
    topic: Topic.addToBetslip,
    headers: { publisher: "survey-betslip" },
    data: { betSlipData }
  });

export const Survey = () => {
  const isMobile = useSelector(UserContextSelectors.isMobile);
  const clientInitialized = useSelector(UiSelector.clientInitialized);
  const [loaded, setLoaded] = useState(false);
  const [clientInitSuccessSubscription, setClientInitSuccessSubscription] =
    useState(null);
  const { isSurveyEnabled, findIfSurveyIsVisibleByUrl, sdkUrl } = useSurvey();
  const { name } = PageContextUtils.getClientProviderData();

  const surveyAvailable = useMemo(
    () => loaded && (name !== "Kambi" || clientInitialized),
    [clientInitialized, loaded, name]
  );
  const [isSurveyVisible, setIsSurveyVisible] = useState(false);

  useEffect(() => {
    if (surveyAvailable)
      if (isMobile) {
        toggleBetslipVisibility(!isSurveyVisible);
      } else if (!isSurveyVisible) {
        toggleBetslipVisibility(true);
      }
  }, [surveyAvailable, isMobile, isSurveyVisible]);

  useEffect(() => {
    const isSurveyVisible = findIfSurveyIsVisibleByUrl(window.location.href);
    if (isSurveyVisible) toggleSpinner(isSurveyVisible, "Survey");
    setIsSurveyVisible(isSurveyVisible);
  }, [findIfSurveyIsVisibleByUrl]);

  useAppContentHider(isSurveyVisible);

  const scriptData = useMemo(() => {
    return {
      attributes: {
        type: "text/javascript",
        language: "JavaScript",
        src: isSurveyEnabled && sdkUrl,
        async: true,
        "data-ot-ignore": ""
      },
      handlers: {
        onload: () => {
          setLoaded(true);
        }
      }
    };
  }, [isSurveyEnabled, sdkUrl]);

  const onBetSlipClick = useCallback(
    betslipInfo => {
      clientInitSuccessSubscription?.unsubscribe();

      GeneralUtils.promiseTimeout(
        1000,
        new Promise(resolve => {
          setClientInitSuccessSubscription(
            createClientInitSubscription(() => {
              toggleSpinner(false, "survey-betslip", false);
              resolve();

              clientInitSuccessSubscription?.unsubscribe();
            })
          );
        })
      );

      setIsSurveyVisible(false);
      toggleSpinner(true, "survey-betslip");

      setTimeout(() => {
        TrackingUtils.sendEvent({
          event: "survey-openbetslipclick",
          ...betslipInfo
        });

        if (isMobile) {
          toggleBetslipVisibility(true);
        }

        addToBetslip(betslipInfo);
      }, 0);
    },
    [clientInitSuccessSubscription, isMobile]
  );

  useEffect(() => {
    const subscription = subscribeToOpenSurvey(() => {
      if (!isSurveyVisible) {
        toggleSpinner(true, "Survey", true);
        setIsSurveyVisible(true);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [isSurveyVisible]);

  useEffect(() => {
    const subscription = subscribeToNavigation(url => {
      const isSurveyVisible = findIfSurveyIsVisibleByUrl(url);

      if (!isSurveyVisible) {
        toggleSpinner(isSurveyVisible, "Survey", true);
        setIsSurveyVisible(isSurveyVisible);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [findIfSurveyIsVisibleByUrl]);

  useScript(scriptData, isSurveyVisible);

  return (
    (surveyAvailable && (
      <SurveyOnDemand
        isSurveyVisible={isSurveyVisible}
        onBetSlipClick={onBetSlipClick}
        toggleSpinner={toggleSpinner}
      />
    )) ||
    null
  );
};
